import { Button, ButtonGroup } from "@components/button";
import { IDayAction } from "@components/calendar/Calendar.utils";
import { formatDateToDateString, getMonthDays, isSameDay } from "@components/inputs/date/utils";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext from "@odata/BindingContext";
import {
    EntitySetName,
    IPrAbsenceIllnessDetailEntity,
    IPrAttendanceDayIntervalEntity,
    IPrAttendanceEntity,
    PrAbsenceIllnessDetailEntity,
    PrAttendanceEntity
} from "@odata/GeneratedEntityTypes";
import { PrAttendanceStatusCode, PrWorkIntervalTypeCode } from "@odata/GeneratedEnums";
import { viewSwitchPath, WorkedHoursPath } from "@pages/payroll/attendance/Attendance.def";
import { isDefined } from "@utils/general";
import { TRecordAny } from "global.types";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import DateType, { DateFormat, getUtcDayjs } from "../../../types/Date";
import { IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import {
    createNewAttendanceDayEntity,
    getDaySaldo,
    getMonthHolidays,
    getWorkingPattern,
    getWorkingPatternDaysMap,
    pairIntervalsWithExistingIds,
    parseDayActionsFromData,
    refreshBalance,
    transformWorkIntervalsBeforeSave,
    updateDaySaldo
} from "./Attendance.utils";

export interface IAttendanceCustomData extends IFormStorageDefaultCustomData {
    selectedDays: Set<number>;
    saldoMap: Record<number, number>;
    hoursFundMap: Record<number, number>;
    actions?: Record<number, IDayAction[]>;
    holidays?: Date[];
    absenceIllnesses?: Record<number, IDayAction[]>;
}

class AttendanceFormView extends FormViewForExtend<IPrAttendanceEntity, IFormViewProps<IPrAttendanceEntity, IAttendanceCustomData>> {
    static contextType = AppContext;
    static defaultProps = {
        title: "Attendance:FormTitle",
    };

    get payslipDayjs() {
        // return first day of the month - note, do not use actual day,
        // as it may behave differently according to current day in month, e.g. 31.1. -> February
        return getUtcDayjs().set("date", 1).set("month", this.entity.Month - 1).set("year", this.entity.Year);
    }

    getFormSubtitle = (): string => {
        return `/${DateType.format(this.payslipDayjs, DateFormat.monthAndYear)}/`;
    }

    async onAfterLoad() {
        const storage = this.props.storage;
        storage.setDefaultValueByPath(viewSwitchPath);

        const monthDate = this.payslipDayjs;

        const holidays = await getMonthHolidays(monthDate, this.entity.Year, this.context.getCompany().CountryCode);
        const workingPattern = getWorkingPattern(this.entity);

        let hoursFundMap: Record<number, number> = {};
        if (isDefined(workingPattern)) {
            hoursFundMap = getWorkingPatternDaysMap(workingPattern, monthDate, holidays);
        }

        const monthDays = getMonthDays(monthDate);
        const saldoMap = monthDays.reduce((map: Record<number, number>, day) => {
            const dayIndex = day.get("date");
            const dayHoursFund = hoursFundMap[dayIndex] ?? 0;
            const attendanceDay = this.entity.Days?.find(d => isSameDay(day, getUtcDayjs(d.Date)));
            if (attendanceDay) {
                map[dayIndex] = getDaySaldo(attendanceDay, dayHoursFund);
            } else {
                map[dayIndex] = 0;
            }
            return map;
        }, {});

        const absenceIllnesses = await this.getAbsenceIllness(); // e-neschopenka

        const actions = parseDayActionsFromData(this.entity.Days);

        storage.setCustomData({
            holidays,
            saldoMap,
            hoursFundMap,
            actions,
            absenceIllnesses,
            selectedDays: null
        });
        refreshBalance(storage);

        return super.onAfterLoad();
    }

    getAbsenceIllness = async (): Promise<Record<number, IDayAction[]>> => {
        const dateFilter = `(${PrAbsenceIllnessDetailEntity.DateTo} eq null OR ${PrAbsenceIllnessDetailEntity.DateTo} ge ${formatDateToDateString(this.payslipDayjs.toDate())})`;
        const res = await this.props.storage.oData.getEntitySetWrapper(EntitySetName.PrAbsenceIllnessDetails)
                .query()
                .filter(`${dateFilter} AND ${PrAbsenceIllnessDetailEntity.Employee}/Id eq ${this.entity.Employment.Employee.Id}`)
                .select(`${PrAbsenceIllnessDetailEntity.DateFrom}, ${PrAbsenceIllnessDetailEntity.DateTo}`)
                .fetchData<IPrAbsenceIllnessDetailEntity[]>();

        const actions: Record<number, IDayAction[]> = {};

        res.value.forEach(item => {
            const start = getUtcDayjs(item.DateFrom);
            const end = getUtcDayjs(item.DateTo);
            const startDay = start.get("date");
            const endDay = end.get("date");

            for (let i = startDay; i <= endDay; i++) {
                if (!actions[i]) {
                    actions[i] = [];
                }
                actions[i].push({
                    color: "C_CHART_red",
                    title: "Nemoc",
                    isFullDay: true,
                    isStart: i === startDay,
                    isEnd: i === endDay,
                    isIllness: true
                });
            }
        });

        return actions;
        // return {
        //     5: [{
        //         color: "C_CHART_red",
        //         title: "e-neschopenka",
        //         isFullDay: true,
        //         isStart: true,
        //         isEnd: false,
        //         isIllness: true
        //     }],
        //     6: [{
        //         color: "C_CHART_red",
        //         title: "e-neschopenka",
        //         isFullDay: true,
        //         isStart: false,
        //         isEnd: true,
        //         isIllness: true
        //     }]
        // };
    };

    handleChange = (e: ISmartFieldChange): void => {
        this.props.storage.handleChange(e);
        this.props.storage.refresh();
    };

    handleUnlock = async (): Promise<void> => {
        this.props.storage.setValueByPath(PrAttendanceEntity.StatusCode, PrAttendanceStatusCode.ToProcess);
        await this.save();
    };

    handleLock = async (): Promise<void> => {
        this.props.storage.setValueByPath(PrAttendanceEntity.StatusCode, PrAttendanceStatusCode.Locked);
        await this.save();
    };

    handleSave = async (): Promise<void> => {
        await this.save();
    };

    onBeforeSave = (): IPrAttendanceEntity => {
        const storage = this.props.storage;
        let entity = cloneDeep(this.entity);
        entity.Overtime = Math.max(entity.Balance, 0);
        for (const day of entity.Days) {
            day.Intervals = transformWorkIntervalsBeforeSave(day.Intervals);
        }

        entity = pairIntervalsWithExistingIds(storage, entity);

        return entity;
    };

    clearAttendance = () => {
        const { storage } = this.props;
        storage.setCustomData({ actions: {} });

        const entity = storage.data.entity;
        const workingPattern = getWorkingPattern(this.entity);
        const hoursFundMap: Record<number, number> = storage.getCustomData().hoursFundMap;
        const defaultHour = workingPattern.DefaultDayStart;

        const INTERVAL_LENGTH = 4.5;
        const BREAK_LENGTH = 0.5;

        for (const [key, value] of Object.entries(hoursFundMap)) {
            const day = parseInt(key);
            let dayEntity = entity.Days.find(d => getUtcDayjs(d.Date).get("date") === day);
            if (!dayEntity) {
                if (value === 0) {
                    continue;
                }

                dayEntity = createNewAttendanceDayEntity(day, storage);
            }
        }

        storage.data.entity.Days?.forEach((day, index: number) => {
            // TODO : FOR NOW no crossing to other day
            const dayQuota = hoursFundMap[getUtcDayjs(day.Date).get("date")];
            const intervals: IPrAttendanceDayIntervalEntity[] = [];

            let remainingQuota = dayQuota;
            let startingHour = defaultHour;
            let isBreak = false;
            let counter = 0;
            while (remainingQuota > 0) {
                const interval = isBreak ? BREAK_LENGTH : INTERVAL_LENGTH;
                const currentRemainingQuota = Math.min(remainingQuota, interval);
                const day = getUtcDayjs(startingHour);
                const endingHour = day.add(currentRemainingQuota, "hour");

                intervals.push({
                    [BindingContext.NEW_ENTITY_ID_PROP]: counter++,
                    WorkType: {
                        Code: isBreak ? PrWorkIntervalTypeCode.MealBreak : PrWorkIntervalTypeCode.Work
                    },
                    TimeStart: startingHour,
                    TimeEnd: endingHour.toDate(),
                    IsEndNextDay: false,
                    IsStartNextDay: false,
                    IsWholeDay: false
                });

                startingHour = endingHour.toDate();
                remainingQuota = isBreak ? remainingQuota : remainingQuota - interval;
                isBreak = !isBreak;
            }

            day.Intervals = intervals;
            (day as TRecordAny)[WorkedHoursPath] = getUtcDayjs(startingHour).set("hour", dayQuota).toDate();

            updateDaySaldo(day, storage);
        });

        entity.Balance = 0;

        storage.refresh();
    }

    renderButtons = (): ReactElement => {
        const isEditable = this.entity.Status?.Code === PrAttendanceStatusCode.ToProcess;
        const isProcessed = this.entity.Status?.Code === PrAttendanceStatusCode.Processed;
        return <ButtonGroup wrap={"wrap"}>
            {isEditable && <>
                <Button onClick={this.clearAttendance}
                        isTransparent>{this.props.storage.t("Attendance:Form.Clear")}</Button>

                <Button onClick={this.handleSave}
                        isTransparent>{this.props.storage.t("Attendance:Form.Save")}</Button>
                <Button onClick={this.handleLock}>{this.props.storage.t("Attendance:Form.SaveAndLock")}</Button>
            </>}
            {!isEditable && <Button onClick={this.handleUnlock}
                                    isDisabled={isProcessed}>{this.props.storage.t("Attendance:Form.Unlock")}</Button>}
        </ButtonGroup>;
    };
}

export default withPermissionContext(AttendanceFormView);